/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  JobDetailsModel,
} from '@gh/builder-shared';
import { BuilderPermission, SystemRoleBit } from '@gh/builder-shared-data';
import { ActionFlow, getRouteInitData, TYPING_DELAY } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService, translationTuple } from '@gh/core-mls';
import { DialogService, getControlValue, NotificationService, WaitingService } from '@gh/core-ui';
import { debounceTime$, filter$, map$, skip$, switchMap$ } from '@gh/rx/operators';
import { assign, isNil, negate } from 'lodash';
import { JobBasicInfo, UserListItem } from '../../../resources/job-details-tab';
import { JobDetailsTabBusinessService } from '../../../resources/job-details-tab/job-details-tab-business.service';
import {
  ContracturalCompletionResponse,
  JobDatesTabInfo,
} from '../../../resources/job-details-tab/job-details-tab-model';
import { SelectSupervisorDialogComponent } from '../select-supervisor-dialog/select-supervisor-dialog.component';
import { JobDetailsDatesTabFormState } from './job-details-dates-tab-form.service';

type JobDatesTabSaveFlowState = FormFlowState<JobDatesTabInfo> & { basicInfo?: JobBasicInfo };

@Component({
  standalone: false,
  selector: 'gh-job-details-dates-tab',
  templateUrl: 'job-details-dates-tab.component.html',
  styleUrls: ['job-details-dates-tab.component.scss'],
  providers: [JobDetailsDatesTabFormState],
})
export class JobDetailsDatesTabComponent extends AbstractFormComponent<JobDatesTabInfo> implements OnInit {
  jobId: ID;
  pendingCompletionDate = false;
  datesInfo: JobDatesTabInfo;

  constructor(public form: JobDetailsDatesTabFormState,
              private model: JobDetailsModel,
              private waitings: WaitingService,
              private route: ActivatedRoute,
              private translations: TranslationService,
              private userContext: BuilderUserContext,
              private businessService: JobDetailsTabBusinessService,
              private waitingService: WaitingService,
              private notifications: NotificationService,
              private pipelines: FormActionPipelines,
              private actions: CommonActions,
              private dialogService: DialogService,
              public navigationService: BuilderNavigationService) {
    super();
  }

  get isDisplayWorkingNonWorkingDays(): boolean {
    return this.form.isDisplayWorkingNonWorkingDays;
  }

  get nonWorkingDays(): string {
    const { sundaysAndSaturdays, nonWorkingDays } = this.form;
    return `${this.translations.get('lbl_including')} ${nonWorkingDays}
      ${this.translations.get('lbl_non_working_days')} ${sundaysAndSaturdays} ${this.translations.get('lbl_ss')}`;
  }

  get canUncomplete(): boolean {
    return this.form.isJobEditable
      && this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.UNCOMPLETE_COMPLETE)
      && !isNil(getControlValue(this.form.group, 'completedDate'))
      && !getControlValue(this.form.group, 'isFinalized')
      && !getControlValue(this.form.group, 'isNotProceeding');
  }

  get canComplete(): boolean {
    return this.form.isJobEditable
      && this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.UNCOMPLETE_COMPLETE)
      && isNil(getControlValue(this.form.group, 'completedDate'))
      && !getControlValue(this.form.group, 'isNotFullyPaidContract');
  }

  get canUnfinalize(): boolean {
    return !this.form.isJobEditable &&
      this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.UNFINALIZE) &&
      getControlValue(this.form.group, 'isFinalized') &&
      !!this.datesInfo.finalizedDate;
  }

  get completeFlow(): ActionFlow<JobDatesTabSaveFlowState> {
    return this.pipelines.complete({
      execute: this.actions.async((state: JobDatesTabSaveFlowState) =>
        // if finalized date was set => job will be finalized =>
        // => we have to take actual basic info and update tab container
        this.businessService.updateDatesTabInfo({
          ...state.form.submitValue,
        }).pipe(map$(({ basicInfo, datesInfo }) => ({ ...state, value: datesInfo, basicInfo })))),
      finish: (state: JobDatesTabSaveFlowState) => {
        const { value, basicInfo } = state;
        if (basicInfo) {
          this.model.updateBasicInfo(basicInfo);
        }
        this.init(value);
      },
    });
  }

  ngOnInit(): void {
    const { datesInfo, jobId } = getRouteInitData(this.route);
    assign(this, { jobId });

    this.init(datesInfo);
    this.updateContracturalCompletionSubscription();
  }

  onFinanceApprovedChanged(): void {
    this.form.updateFinanceApprovedDate();
  }

  onContractChanged(): void {
    this.form.updateContractDate();
  }

  onCouncilApprovedChanged(): void {
    this.form.updateCouncilApprovedDate();
  }

  onAuthToCommenceDateChanged(): void {
    this.form.updateAuthToCommenceDate();
  }

  onAuthToCommenceChanged(): void {
    this.form.updateAuthToCommenceDate();

    if (this.form.isAuthToCommence) {
      this.selectSupervisor();
    }
  }

  onCocDateChanged(): void {
    // this.form.updateCocIssueDate();
  }

  onCocComplianceChanged(): void {
    this.form.updateCocIssueDate();
  }

  onFinalizeChange(): void {
    this.form.updateFinalizedDate();
  }

  onContractStartOverridenChange(): void {
    this.form.updateContractStartDate();
  }

  onUpdateCompletedDate(): void {
    this.form.updateCompletedDate();
  }

  onNotProceedingDateChange(): void {
    this.form.updateNotProceedingDate();
  }

  onNotProceedingChange(): void {
    this.form.updateNotProceedingDate();
  }

  onHandoverDateChange(): void {
    const updateSupervisor = (user?: UserListItem) => {
      if (isNil(user)) {
        this.form.restoreOriginalHandoverDate();
      } else {
        this.form.setSupervisor(user);
      }
    };

    this.form.updateHandoverDate();

    if (isNil(this.form.supervisorId)) {
      this.dialogService.openWithInput(SelectSupervisorDialogComponent, {
        systemRole: SystemRoleBit.JobAdmin,
      }).afterClosed()
        .subscribe(updateSupervisor);
      return;
    }
    this.businessService.isSuperVisorAdministrator(this.form.supervisorId!).pipe(
      (this.waitingService.operator()),
      filter$(negate(Boolean)),
      switchMap$(() => this.dialogService.openWithInput(SelectSupervisorDialogComponent, {
        systemRole: SystemRoleBit.Supervisor,
      }).afterClosed()))
      .subscribe(
        updateSupervisor,
        this.notifications.processAll);
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.jobId,
      entityType: 'JOB',
      label: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
    });
  }

  onUncomplete(): void {
    this.form.uncomplete();
  }

  onComplete(): void {
    this.form.complete();
  }

  onUnfinalize(): void {
    this.businessService.unfinalize(this.jobId, this.form.version).pipe(
      switchMap$((basicInfo) => this.businessService.getDatesInfo().pipe(map$((datesInfo) => [basicInfo, datesInfo]))),
      (this.waitings.operator<any>()))
      .subscribe(
        ([basicInfo, datesInfo]) => {
          this.model.updateBasicInfo(basicInfo);
          this.init(datesInfo);
        },
        this.notifications.processAll);
  }

  private init(datesInfo: JobDatesTabInfo): void {
    this.datesInfo = {...datesInfo};
    this.form.build(this.model.basicInfo, datesInfo);
  }

  private updateContracturalCompletionSubscription(): void {
    const completionDateDeps$ = this.form.contracturalCompletionRequest$.pipe(skip$(1));
    completionDateDeps$
      .subscribe(() => this.pendingCompletionDate = true);
    completionDateDeps$.pipe(
      debounceTime$(TYPING_DELAY),
      switchMap$((request) => this.businessService.getContracturalCompletion({
        ...request,
        constructionDays: request.constructionDays || 0,
        timeExtensionLetterDays: request.timeExtensionLetterDays || 0,
      })))
      .subscribe(
        (response: ContracturalCompletionResponse) => {
          this.pendingCompletionDate = false;
          this.form.setContracturalCompletionInfo(response);
        },
        (error: any) => {
          this.pendingCompletionDate = false;
          this.notifications.processAll(error);
        });
  }

  private selectSupervisor(): void {
    if (this.form.isSupervisorExist) {
      this.dialogService.openWithInput(
        SelectSupervisorDialogComponent,
        {
          systemRole: SystemRoleBit.Supervisor,
        },
        { disableClose: true }).afterClosed().pipe(
        filter$(Boolean))
        .subscribe((user: any) => this.form.setSupervisor(user));
    } else {
      this.dialogService.warning('msg_job_no_supervisors')
        .subscribe(() => this.form.resetAuthToCommenceDateToOriginal());
    }
  }
}
